import type {ComponentType, ReactNode} from 'react';
import {renderToString} from 'react-dom/server';

import {PolarisProvider} from './provider';

/**
 * Document shell + SSR/hydration pair for Hono apps (identity, scaffolded apps).
 *
 * The client hydrates the WHOLE document (Remix-style hydrateRoot(document, …)), so
 * this tree must be deterministic and identical on both sides: no Date.now, no random
 * ids, and every locale-formatted string preformatted in the route handler (workerd's
 * ICU ≠ the browser's). Page props cross over as JSON in `window.__PAGE__`.
 */

export interface PagePropsBase {
  title: string;
}

export interface PagePayload {
  page: string;
  props: unknown;
}

/** `<` must not survive into the inline script (a `</script>` in user data would
 *  break out of the tag). */
const serialize = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c');

export function Shell({
  title,
  page,
  props,
  children,
}: {
  title: string;
  page: string;
  props: unknown;
  children?: ReactNode;
}) {
  const payload = serialize({page, props} satisfies PagePayload);
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="noindex, nofollow" />
        <title>{title}</title>
        <link rel="stylesheet" href="/assets/polaris.css" />
        <script defer src="/assets/app.js" />
        {/* The hydration payload as a NON-EXECUTABLE JSON island. A `type=application/json`
            script is data, not code — the browser never executes it, so it is exempt from
            CSP `script-src`. That lets the app ship a strict `script-src 'self'` with no
            'unsafe-inline' and no per-request nonce: nothing inline runs. readPagePayload
            reads it by id. suppressHydrationWarning covers the JSON text node. */}
        <script
          type="application/json"
          id="__PAGE__"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{__html: payload}}
        />
      </head>
      <body>
        <PolarisProvider>{children}</PolarisProvider>
      </body>
    </html>
  );
}

/** Both sides render the same wrapper; `display: contents` keeps it out of layout, so
 *  wrapping a page in StaticBody cannot move anything. */
const ISLAND = {display: 'contents'} as const;
const EMPTY = {__html: ''};

/**
 * Marks a page body as having NOTHING interactive in it, so hydration skips the subtree.
 *
 * This is an MPA — every navigation re-hydrates the whole document — so blocking time
 * scales with rendered node count. On a page whose only interactive parts are the AppFrame
 * chrome (user menu, mobile nav toggle), React was walking the entire body to make a
 * dropdown work. Measured on /admin/audit (150 rows, 4x CPU throttle, median of 3):
 * main-thread JS 353ms → 218ms, and the longest single JS call — the one that decides
 * whether a click lands — 194ms → 108ms.
 *
 * NOTE (2026-07-23): that page has since become an interactive IndexFilters console and
 * hydrates normally again, and the dashboard hydrates so Polaris Avatar can paint its
 * image — so NO page currently uses this. It's kept as the right tool for a genuinely
 * read-only SSR'd page; the measurement above is why it exists.
 *
 * The client still *constructs* the body's elements before this throws them away. A
 * variant taking a render function, so it never builds them at all, was implemented and
 * measured: 207ms vs 218ms, i.e. inside the run-to-run noise. createElement is cheap;
 * what costs is fiber creation and DOM matching, and that is what this already skips.
 * Don't reach for the uglier API.
 *
 * The mechanism, verified against react-dom 18.3.1 rather than assumed:
 *   - `shouldSetTextContent` returns true for any host element with
 *     `dangerouslySetInnerHTML.__html != null`, so the fiber gets NO child fibers and
 *     hydration never descends into the server's markup;
 *   - `popHydrationState` only deletes unhydrated tail DOM when `!shouldSetTextContent(…)`,
 *     so the server-rendered content is explicitly exempt from being cleared;
 *   - the `dangerouslySetInnerHTML` branch of `diffHydratedProperties` only *warns* (dev
 *     only, silenced here); it never emits an update payload, so nothing overwrites the
 *     DOM — and on a later re-render `'' === ''`, so it stays put.
 *
 * CONSTRAINT: this is for whole-document hydration of a server-rendered MPA page only.
 * Never use it in a client-routed app (recipes, React Router): a client-side navigation
 * would render the empty branch with no server HTML underneath it, i.e. a blank page.
 *
 * Re-measure with `bun --filter @slj/identity bench:hydration` after touching this.
 */
export function StaticBody({children}: {children?: ReactNode}) {
  // Probed off globalThis, not the bare `document`, for the same reason readPagePayload
  // is: consumer apps typecheck against workerd's globals, which have no DOM lib.
  const server = typeof (globalThis as {document?: unknown}).document === 'undefined';
  if (server) {
    return (
      <div data-static-body="" style={ISLAND}>
        {children}
      </div>
    );
  }
  return <div data-static-body="" style={ISLAND} suppressHydrationWarning dangerouslySetInnerHTML={EMPTY} />;
}

export function renderPage<P extends PagePropsBase>(
  page: string,
  Page: ComponentType<P>,
  props: P,
): string {
  return (
    '<!doctype html>' +
    renderToString(
      <Shell title={props.title} page={page} props={props}>
        <Page {...props} />
      </Shell>,
    )
  );
}

/** Client side of the pair — read what the server serialized into the `__PAGE__`
 *  JSON island. Reached through globalThis.document (cast, not the DOM lib) so this
 *  module keeps typechecking under consumers with no DOM types. */
export function readPagePayload<P>(): {page: string; props: P} {
  const el = (
    globalThis as {
      document?: {getElementById(id: string): {textContent: string | null} | null};
    }
  ).document?.getElementById('__PAGE__');
  const raw = el?.textContent;
  if (!raw) {
    throw new Error('__PAGE__ missing — the document was not rendered by renderPage()');
  }
  const payload = JSON.parse(raw) as {page: string; props: P};
  if (typeof payload.page !== 'string') {
    throw new Error('__PAGE__ malformed — the document was not rendered by renderPage()');
  }
  return payload;
}
